import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

import { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { isAxiosError } from '@/api/business'
import { UserTable } from '../..'
import { updateUser } from '@/api/business/user'
import { UserKeys } from '@/queries/useUserQuery'
import Button from '@/components/Button'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from "@/components/ui/select"
import { Input } from '@/components/ui/input'
import { useEffect } from 'react' 
import { Checkbox } from '@/components/ui/checkbox'

import { useBaseStore } from '@/store'
import { authStateSelector } from '@/store/authSlice'

interface EditDialogProps {
    row: Row<UserTable>
    isOpen: boolean
    onClose: () => void
}

const EditDialog = ({ row, isOpen, onClose }: EditDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    const { profile } = useBaseStore(authStateSelector)

    const schema = z.object({
       NM_USUARIO: z.string().min(1, {
            message: 'O campo Nome do Usuário é obrigatório',
        }),
        DD_EMAIL: z.string().min(1, {
            message: 'O campo E-mail é obrigatório',
        }),
        DD_NUMERO_CONTATO:z.string(),
        DS_USUARIO: z.string().min(1, {
            message: 'O campo Usuário de login é obrigatório',
        }),
        DD_STATUS: z.union([z.literal(0), z.literal(1)]),
        DD_PERFIL: z.union([z.literal("NOVO"), z.literal("ADMINISTRADOR"), z.literal("REPRESENTANTE"), z.literal("ADMINISTRADOR_REVENDA")])
    })

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: { 
            NM_USUARIO:  row.original.NM_USUARIO || "", 
            DD_EMAIL: row.original.DD_EMAIL || "", 
            DD_NUMERO_CONTATO: row.original.DD_NUMERO_CONTATO || "", 
            DS_USUARIO: row.original.DS_USUARIO || "",  
            DD_STATUS: row.original.DD_STATUS, 
            DD_PERFIL:  row.original.DD_PERFIL || 'NOVO'
        },
    })

    useEffect(() => {
        form.reset({
            NM_USUARIO:  row.original.NM_USUARIO || "", 
            DD_EMAIL: row.original.DD_EMAIL || "", 
            DD_NUMERO_CONTATO: row.original.DD_NUMERO_CONTATO || "", 
            DS_USUARIO: row.original.DS_USUARIO || "",  
            DD_STATUS: row.original.DD_STATUS, 
            DD_PERFIL:  row.original.DD_PERFIL || 'NOVO'
        })
    }, [row.original])

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: updateUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UserKeys.lists() })
            toast({
                title: 'Revenda atualizado com sucesso',
            })
            onClose()
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível editar a Revenda. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao editar Revenda',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {
        mutate({
            SK_USUARIO: row.original.SK_USUARIO,
           ...data
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[700px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Editar Revenda</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                         <div className='grid grid-cols-[2fr,1fr] gap-4'>
                            <FormField
                                name="NM_USUARIO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nome do usuário</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_NUMERO_CONTATO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Número de Contato</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                        <div className='grid gap-4'>
                            <FormField
                                name="DD_EMAIL"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>E-mail</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                         <div className='grid grid-cols-[2fr,1fr] gap-4'>
                            <FormField
                                name="DS_USUARIO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Login</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_PERFIL"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Perfil</FormLabel>
                                         <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value.toString()} disabled={profile === 'ADMINISTRADOR_REVENDA'}>
                                            <FormControl>
                                                <SelectTrigger>
                                                <SelectValue placeholder="" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value='NOVO'>Novo</SelectItem>
                                                <SelectItem value="ADMINISTRADOR">Administrador</SelectItem>
                                                <SelectItem value="REPRESENTANTE">Representante</SelectItem>
                                                <SelectItem value="ADMINISTRADOR_REVENDA">Administrador da Revenda</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                         <FormField
                                control={form.control}
                                name="DD_STATUS"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-end space-x-3 pb-0.5">
                                        <FormControl>
                                            <Checkbox
                                                checked={(field.value == 1 ? true : false)}
                                                onCheckedChange={(checked) => field.onChange(checked == true ? 1 : 0)}
                                            />
                                        </FormControl>
                                        <div className=" leading-none">
                                            <FormLabel>
                                                Usuário ativo
                                            </FormLabel> 
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />

                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default EditDialog
